import { and, eq, getTableColumns, sql } from "drizzle-orm";
import { Box, Button } from "@mui/material";
import { useDebounce } from "@uidotdev/usehooks";
import cuid from "cuid";
import { useContext, useEffect, useState } from "react";

import { FullInput } from "@/components/Utils.tsx";
import { db } from "@/db/db.ts";
import { fleetGroup, fleetGroupItem } from "@/db/schema.ts";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import { TrackingContext } from "@/models/TrackingStateProvider.tsx";

export default function FleetMessageSuccess({
  temporaryChannels,
  setShowSuccessMessage,
  saveGroupForFutureUse,
  setSaveGroupForFutureUse,
}) {
  const { ampli } = useContext(TrackingContext);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<string>("");
  const [groupCreated, setGroupCreated] = useState<boolean>(false);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const [invalidGroupName, setInvalidGroupName] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);
  const groupNameDebounced = useDebounce(groupName, 800);

  const validateGroupName = async (name: string) => {
    const existingGroup = await db
      .select()
      .from(fleetGroup)
      .where(and(eq(sql`lower(${fleetGroup.name})`, name?.toLowerCase()), eq(fleetGroup.workspaceId, workspaceId)));
    return !!existingGroup[0]?.id;
  };

  useEffect(() => {
    if (!groupNameDebounced) {
      return;
    }

    validateGroupName(groupNameDebounced).then((value) => {
      setDisabled(() => false);
      setInvalidGroupName(() => value);
    });
  }, [groupNameDebounced, workspaceId]);

  const resetState = () => {
    setGroupCreated(() => false);
    setGroupName(() => "");
    setDisabled(() => false);
    setInvalidGroupName(() => false);
    setError(() => false);
    setSaveGroupForFutureUse(false);
    setShowSuccessMessage(false);
  };

  const createNewFeedGroupWithItems = async (trimmedGroupName: string) => {
    if (!temporaryChannels || temporaryChannels.length === 0) {
      return Promise.reject("No channels to add to group");
    }
    try {
      const newFeedGroup = await db
        .insert(fleetGroup)
        .values({
          id: cuid(),
          name: trimmedGroupName,
          workspaceId,
          createdAt: new Date().toISOString(),
        })
        .returning({
          ...getTableColumns(fleetGroup),
        });

      const newFeedGroupId = newFeedGroup[0]?.id;
      if (newFeedGroupId) {
        const values = temporaryChannels.map((feedId: string) => ({
          id: cuid(),
          fleetGroupId: newFeedGroupId,
          feedId,
          createdAt: new Date().toISOString(),
        }));
        try {
          await db.insert(fleetGroupItem).values(values);
        } catch (e) {
          console.log({ e });
          return Promise.reject(e);
        }
      }
    } catch (e) {
      console.log({ e });
      return Promise.reject(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedGroupName = groupNameDebounced.trim();
    setGroupName(() => trimmedGroupName);
    const validGroupName = await validateGroupName(trimmedGroupName);
    setInvalidGroupName(() => validGroupName);
    if (validGroupName || trimmedGroupName?.length === 0) {
      return;
    }
    setDisabled(() => true);
    setError(() => null);
    try {
      await createNewFeedGroupWithItems(trimmedGroupName);
      ampli.groupNameSave({ name: trimmedGroupName });
      setGroupCreated(() => true);
      setTimeout(() => {
        resetState();
      }, 5000);
    } catch (e) {
      console.error({ e });
      setError(() => e);
      setDisabled(() => false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: "450px" }}>
          {!groupCreated && (
            <Box sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}>
              Fleet Message sent to {temporaryChannels?.length} recipients successfully!
            </Box>
          )}

          {saveGroupForFutureUse ? (
            <Box>
              {groupCreated ? (
                <Box sx={{ textAlign: "center" }}>
                  <Box sx={{ fontSize: "1.5rem", mb: 1 }}>Group Created</Box>
                  <Box>
                    <Button onClick={() => resetState()} variant="outlined">
                      Dismiss
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box component="form" onSubmit={(e) => handleSubmit(e)}>
                  <FullInput
                    label="Group Name"
                    value={groupName}
                    error={invalidGroupName}
                    helperText={invalidGroupName && <Box sx={{ mt: -2 }}>Group name already exists</Box>}
                    onChange={(e) => {
                      setDisabled(() => true);
                      setGroupName(e.target.value);
                    }}
                    sx={{ mb: 2 }}
                  />
                  {error && <Box sx={{ background: "red", mb: 2 }}>{JSON.stringify(error, null, 2)}</Box>}

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button disabled={disabled || groupName?.length === 0} type="submit" variant="contained">
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={disabled}
                      onClick={() => {
                        resetState();
                        ampli.groupNameCancel();
                      }}
                      className="basic-button variant-outlined"
                      sx={{ flexGrow: 1 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <Box>
                <Button onClick={() => resetState()} variant="outlined">
                  Dismiss
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
